import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import { AWARDS_QUERY_KEY, awardQuery, awardsQuery, forViewer, viewerIdOf } from "../services/queries";
import type { AwardDetails, Awardee } from "../domain/certificates/awards";
import { failureDetail } from "./useCertificateTemplates";
import { useCertificatesWrite } from "./useCertificatesWrite";

export const useAwards = () => {
  const { user, token } = useAuth();
  return useQuery({
    ...forViewer(awardsQuery(user?.schoolId, token), viewerIdOf(user)),
    enabled: !!token && !!user?.schoolId,
  });
};

/** One Award with its Certificates. */
export const useAward = (awardId: string | undefined) => {
  const { user, token } = useAuth();
  return useQuery({
    ...forViewer(awardQuery(user?.schoolId, token, awardId), viewerIdOf(user)),
    enabled: !!token && !!user?.schoolId && !!awardId,
  });
};

/**
 * A Family child's sent certificates, found by name and linked parents. Kept
 * under the Award key so sending or deleting a certificate refreshes it.
 */
export const useChildCertificates = (child: Awardee) => {
  const { user, token } = useAuth();
  return useQuery({
    queryKey: [
      ...AWARDS_QUERY_KEY,
      user?.schoolId,
      "child",
      child.firstName,
      child.surname,
      child.parentUserIds,
    ],
    queryFn: async () => {
      if (!token) throw new Error("No token");
      const response = await api.certificates.listChildCertificates(
        child,
        token,
      );
      return response.certificates ?? [];
    },
    enabled: !!token && !!user?.schoolId && child.parentUserIds.length > 0,
  });
};

/** An Award mutation that refreshes every Award query and shows a toast if it fails. */
const useAwardWrite = <Variables, Result>(
  write: (variables: Variables, token: string) => Promise<Result>,
  failureMessage: string,
) => useCertificatesWrite(write, failureMessage, AWARDS_QUERY_KEY);

/** Creates the Award the first time, then updates it. */
export const useSaveAwardDetails = () =>
  useAwardWrite(
    async (
      {
        awardId,
        details,
      }: {
        /** Missing until the first save. */ awardId?: string;
        details: AwardDetails;
      },
      token,
    ) => {
      const response = awardId
        ? await api.certificates.updateAward(awardId, details, token)
        : await api.certificates.createAward(details, token);
      return response.award;
    },
    "Couldn't save the Award.",
  );

/** Adds Awardees to a saved Award; the server numbers their certificates. */
export const useAddCertificates = () =>
  useAwardWrite(
    async (
      { awardId, awardees }: { awardId: string; awardees: Awardee[] },
      token,
    ) =>
      (await api.certificates.addCertificates(awardId, awardees, token))
        .certificates,
    "Couldn't add the Awardees.",
  );

export const useRemoveCertificate = () =>
  useAwardWrite(
    (
      { awardId, certificateId }: { awardId: string; certificateId: string },
      token,
    ) => api.certificates.removeCertificate(awardId, certificateId, token),
    "Couldn't remove the certificate.",
  );

/** Deletes a sent Certificate; the server withdraws its notices from the Users first. */
export const useDeleteSentCertificate = () =>
  useAwardWrite(
    (
      { awardId, certificateId }: { awardId: string; certificateId: string },
      token,
    ) => api.certificates.removeCertificate(awardId, certificateId, token),
    "Couldn't delete the certificate.",
  );

export const useDeleteAward = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (awardId: string) => {
      if (!token) throw new Error("No token");
      return api.certificates.deleteAward(awardId, token);
    },
    onSuccess: (_result, awardId) => {
      // Drop the deleted one rather than refetching it (that would 404).
      queryClient.removeQueries({
        queryKey: [...AWARDS_QUERY_KEY, user?.schoolId, awardId],
      });
      queryClient.invalidateQueries({
        queryKey: [...AWARDS_QUERY_KEY, user?.schoolId],
        exact: true,
      });
      showMessage({ message: "Award deleted", severity: "success" });
    },
    onError: (error) => {
      console.error("Failed to delete Award:", error);
      showMessage({
        message: "Couldn't delete the Award." + failureDetail(error),
        severity: "error",
      });
    },
  });
};
